import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, TextField, Typography, Button, Chip, CircularProgress, Skeleton,
  FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import BrandedUnitDetailSection from '../../BrandedShareChrome/BrandedUnitDetailSection';
import { colors, typography } from '../../../../styles/theme';
import { useScrollEdgeFade, getScrollEdgeFadeSx } from '../../../../hooks/useScrollEdgeFade';
import UserLotImages from './UserLotImages';
import UserLotVideos from './UserLotVideos';
import UserLotAttachments from './UserLotAttachments';
import UserLotListMultiSelect from './UserLotListMultiSelect';
import UfWizardStepper from './UfWizardStepper';
import UfBuildingTypeStep from './UfBuildingTypeStep';
import UfAttributesForm, { disabledSelectSx } from './UfAttributesForm';
import { getUnitHeaderTitle } from '../../../utils/userLotUnits';
import UfEmptyState from './UfEmptyState';
import StockStatusBadge from '../../../../Showroom/components/StockStatusBadge';
import {
  applyUfPropertyFlatEdit,
  buildUfPropertyEditView,
  buildUfPropertyInfoView,
  resolveSelectedPropertyTypeId,
} from '../../../utils/ufPropertyFields';
import OfficialUnitCadastralBlock from './OfficialUnitCadastralBlock';
import {
  CURRENCY_OPTIONS,
  OPERATIONS,
  OPERATION_LABEL,
  STOCK_STATUSES,
  STOCK_STATUS_LABELS,
  formatPriceInputDisplay,
  parsePriceInputValue,
} from '../../../utils/listingCoreConstants';
import {
  UF_WIZARD_STEP,
  hasUfAttributeDisplayData,
  normalizeBuildingType,
} from '../../../utils/ufAttributeFormUtils';
import { parseOfficialBuiltSurfaceM2 } from '../../../utils/lotFunctionalUnits';
import { PRIVATE_BLOCK_TITLE_DEFAULT } from '../../../utils/spaceMirrorLots';
import {
  BlockCollapseToggle,
  BlockHeader,
  FIELD_HINT_SX,
  FORM_PRIMARY_BUTTON_SX,
  FORM_SECONDARY_BUTTON_SX,
  ORGANIZE_BLOCK_SX,
  PANEL_SECTION_TITLE_SX,
  PRIVATE_BLOCK_SX,
  PUBLICATION_BLOCK_SX,
  DOCK_SCROLLBAR_SX,
  SECTION_BAR_ICON_BUTTON_SX,
  SECTION_BAR_ICON_BUTTON_DELETE_SX,
} from './formBlockUi';

const MAX_TITLE = 100;
const MAX_DESC = 500;
const MAX_OBS = 300;

const UF_LISTING_SKELETON_SX = {
  bgcolor: 'rgba(255,255,255,0.08)',
  borderRadius: '8px',
};

const UfListingStepSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ ...PRIVATE_BLOCK_SX, p: 2 }}>
      <Skeleton variant="text" width={88} height={16} sx={UF_LISTING_SKELETON_SX} />
      <Skeleton variant="text" width="70%" height={12} sx={{ ...UF_LISTING_SKELETON_SX, mt: 0.75 }} />
      <Skeleton variant="rounded" width="100%" height={40} sx={{ ...UF_LISTING_SKELETON_SX, mt: 1.5 }} />
      <Skeleton variant="rounded" width="100%" height={64} sx={{ ...UF_LISTING_SKELETON_SX, mt: 1.5 }} />
    </Box>
    <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2 }}>
      <Skeleton variant="text" width={110} height={16} sx={UF_LISTING_SKELETON_SX} />
      <Skeleton variant="text" width="55%" height={12} sx={{ ...UF_LISTING_SKELETON_SX, mt: 0.75 }} />
      <Skeleton variant="rounded" width="100%" height={40} sx={{ ...UF_LISTING_SKELETON_SX, mt: 1.5 }} />
      <Skeleton variant="rounded" width="100%" height={72} sx={{ ...UF_LISTING_SKELETON_SX, mt: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 1.25, mt: 1.5 }}>
        <Skeleton variant="rounded" width="40%" height={40} sx={UF_LISTING_SKELETON_SX} />
        <Skeleton variant="rounded" width="28%" height={40} sx={UF_LISTING_SKELETON_SX} />
        <Skeleton variant="rounded" width="32%" height={40} sx={UF_LISTING_SKELETON_SX} />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
      <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.45)' }} />
    </Box>
  </Box>
);

const DetailsTab = ({
  lotId,
  userLotData,
  userLotForm,
  setUserLotForm,
  loadingUserLot,
  userLotLists = [],
  onListCreated,
  isEditing,
  isSaving,
  isOwner,
  canView,
  canEdit,
  fieldSx,
  secondaryTextSx,
  onSave,
  onDelete,
  onEditStart,
  onEditCancel,
  onAddUnit,
  onBack,
  readOnly = false,
  ufWizardStep = UF_WIZARD_STEP.BUILDING_TYPE,
  ufBuildingTypes = [],
  ufAttributeSchema = null,
  ufSchemaLoading = false,
  ufListingBootstrapLoading = false,
  onBuildingTypeChange,
  onWizardContinue,
  onWizardBack,
  onWizardGoToStep,
  onEditStartWithWizard,
  accentColor = null,
  collectionCanEdit = false,
  lotContext = null,
  officialUnit = null,
  bodyScrollRef = null,
  spaceOwnerUserId = null,
  spaceGuestCanEditAll = false,
  spaceGuestCanDelete = false,
  privateBlockTitle = PRIVATE_BLOCK_TITLE_DEFAULT,
  children = null,
  /** R1: sección "Consultas y leads" (la arma DockPanel; solo fuera de edición). */
  recordLeads = null,
  recordCampaigns = null,
  /** Comparación de mercado: `({ onEdit }) => node` (la arma DockPanel; solo fuera de edición). */
  renderMarketComparison = null,
}) => {
  const [privateExpanded, setPrivateExpanded] = useState(false);
  const propertyInfoView = useMemo(() => buildUfPropertyInfoView({
    info: userLotForm?.property_info,
    schema: ufAttributeSchema,
    // Shares (readOnly): sin contacto del corredor por ahora.
    hideAgent: readOnly,
  }), [userLotForm?.property_info, ufAttributeSchema, readOnly]);
  const propertyEditView = useMemo(() => buildUfPropertyEditView({
    info: userLotForm?.property_info,
    schema: ufAttributeSchema,
  }), [userLotForm?.property_info, ufAttributeSchema]);
  /** Adaptador: UfAttributesForm edita un form plano; se vuelca en `property_info`. */
  const setPropertyEditForm = (updater) => {
    setUserLotForm((prev) => {
      const flat = buildUfPropertyEditView({
        info: prev.property_info,
        schema: ufAttributeSchema,
          }).form;
      const nextFlat = typeof updater === 'function' ? updater(flat) : updater;
      const changedIds = Object.keys(nextFlat).filter((id) => nextFlat[id] !== flat[id]);
      const errors = { ...(prev.property_errors || {}) };
      changedIds.forEach((id) => { delete errors[id]; });
      return {
        ...prev,
        property_info: applyUfPropertyFlatEdit(prev.property_info, nextFlat),
        property_errors: errors,
      };
    });
  };
  const [organizeExpanded, setOrganizeExpanded] = useState(false);
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();

  // Callback ref estable: si se recrea en cada render, React lo llama con null
  // y con el nodo en cada commit, scrollRef hace setState y entra en bucle
  // («Maximum update depth exceeded» al crear una UF).
  const setBodyScrollNode = useCallback((node) => {
    scrollRef(node);
    if (bodyScrollRef) bodyScrollRef.current = node;
  }, [scrollRef, bodyScrollRef]);

  // "Solo en tu espacio": cerrado en la vista. Al entrar a editar se abre si ya
  // tiene datos; mientras se edita no se cierra solo (lo cierra el usuario).
  const privateFormRef = useRef(userLotForm);
  privateFormRef.current = userLotForm;
  useEffect(() => {
    const form = privateFormRef.current;
    const hasPrivateData = Boolean(
      String(form.unit_label || '').trim()
      || String(form.observations || '').trim(),
    );
    setPrivateExpanded(Boolean(isEditing) && hasPrivateData);
  }, [isEditing, userLotData?.id]);

  useEffect(() => {
    setOrganizeExpanded(
      Array.isArray(userLotForm.list_ids) && userLotForm.list_ids.length > 0,
    );
  }, [userLotForm.list_ids]);

  useEffect(() => {
    refreshScrollEdges();
  }, [
    refreshScrollEdges,
    isEditing,
    ufWizardStep,
    ufListingBootstrapLoading,
    ufSchemaLoading,
    privateExpanded,
    organizeExpanded,
    children,
  ]);

  const wizardBusy = isSaving || ufListingBootstrapLoading;
  // Espacio ajeno: UF que creó el invitado (`can_edit_all`, mig 082) → mismo
  // wizard que el dueño. Otra UF existente → la API solo acepta copy comercial +
  // `stock_status` → formulario acotado (como el editor de colección) más sumar
  // media. Borrar media / portada solo con `can_delete`.
  const spaceGuest = Boolean(spaceOwnerUserId);
  const spaceGuestMediaAdditiveOnly = spaceGuest && !spaceGuestCanDelete;
  const coreEditable = collectionCanEdit
    || (spaceGuest && Boolean(userLotData?.id) && !spaceGuestCanEditAll);
  const coreEdit = Boolean(coreEditable && isEditing);
  // «Editar unidad» de la comparación de mercado: solo con edición completa (precio,
  // superficie, tipo). El formulario acotado (editor de colección / invitado) no los toca.
  const marketEditHandler = canEdit && !readOnly && !coreEditable
    ? (onEditStartWithWizard || onEditStart)
    : undefined;
  const showWizardStepper = isEditing && !readOnly && !coreEdit;
  const showListingBootstrap = isEditing && !readOnly && !coreEdit
    && ufWizardStep === UF_WIZARD_STEP.LISTING
    && ufListingBootstrapLoading;

  const inputLabelSx = {
    color: 'rgba(255,255,255,0.72)',
    fontFamily: typography.fontFamily.primary,
    '&.Mui-focused': { color: '#ffffff' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.72)' },
  };
  const ufFieldSx = {
    ...fieldSx,
    '& .MuiOutlinedInput-root': {
      ...(fieldSx?.['& .MuiOutlinedInput-root'] || {}),
      backgroundColor: '#242424',
      borderRadius: '8px',
      '& fieldset': { borderColor: '#4A4A4A' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
      '&.Mui-focused fieldset': { borderColor: colors.primary },
    },
  };
  const selectSx = {
    backgroundColor: '#242424',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontFamily: typography.fontFamily.primary,
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.65)' },
    '& .MuiSelect-select': { color: '#ffffff' },
    '&.Mui-disabled .MuiSelect-select': {
      WebkitTextFillColor: 'rgba(255,255,255,0.82)',
      color: 'rgba(255,255,255,0.82)',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4A4A4A' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
    // Selects bloqueados (Moneda / Operación / Estado fuera de edición): look de solo lectura.
    ...disabledSelectSx,
  };
  const menuItemSx = {
    color: '#ffffff',
    fontSize: '13px',
    height: '34px',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
    '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
  };
  const listSelectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 360,
        bgcolor: '#1d1d1d',
        border: '1px solid rgba(255,255,255,0.10)',
        backgroundImage: 'none',
        '& .MuiMenuItem-root': {
          py: 0.75,
        },
      },
    },
  };

  if (loadingUserLot) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress sx={{ color: '#ffffff' }} />
      </Box>
    );
  }


  if (!canView && !canEdit) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '180px', gap: 1.5, px: 3, textAlign: 'center'
      }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily.primary }}>
          Este lote no tiene información adicional disponible.
        </Typography>
      </Box>
    );
  }

  // Sin datos registrados y no está editando → empty state
  if (!userLotData && !isEditing) {
    return (
      <UfEmptyState
        onAdd={onAddUnit}
        showAction={canEdit && Boolean(onAddUnit)}
      />
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      alignSelf: 'stretch',
    }}
    >

      {/* Chrome fijo: fuera del scroll (mask-image no lo debe cubrir). */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        px: 2,
        py: 1,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        zIndex: 2,
        flexShrink: 0,
        flexWrap: 'nowrap',
        bgcolor: colors.backgroundSurface,
      }}
      >
        <Typography
          sx={{
            ...PANEL_SECTION_TITLE_SX,
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {readOnly
            ? (userLotForm.title || 'Unidad')
            : getUnitHeaderTitle(userLotForm, officialUnit)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
          {isOwner && isEditing && !readOnly && userLotData?.id && (
            <Tooltip title="Eliminar" placement="top" arrow>
              <IconButton
                size="small"
                onClick={onDelete}
                aria-label="Eliminar unidad"
                sx={SECTION_BAR_ICON_BUTTON_DELETE_SX}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
          {((canEdit && !isEditing && !readOnly)
            || (coreEditable && !isEditing && userLotData?.id)) && (
            <Tooltip title="Editar" placement="top" arrow>
              <IconButton
                size="small"
                onClick={coreEditable ? onEditStart : (onEditStartWithWizard || onEditStart)}
                aria-label="Editar unidad"
                sx={SECTION_BAR_ICON_BUTTON_SX}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {onBack && (
            <Tooltip title="Volver a unidades" placement="top" arrow>
              <IconButton
                size="small"
                onClick={onBack}
                aria-label="Volver a la lista de unidades"
                sx={SECTION_BAR_ICON_BUTTON_SX}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Stepper del wizard fuera del scroll: fijo bajo el header de la unidad. */}
      {showWizardStepper && (
        <Box
          sx={{
            flexShrink: 0,
            px: 2.25,
            pt: 2.5,
            // Mismos offsets que tenía dentro del cuerpo (p: 2 + py: 0.5 + gap 2).
            pb: 0.75,
            bgcolor: colors.backgroundSurface,
          }}
        >
          <UfWizardStepper
            activeStep={ufWizardStep}
            onStepClick={onWizardGoToStep}
            disabled={wizardBusy}
          />
        </Box>
      )}

      {/* Formulario: único nodo con overflow + fade */}
      <Box
        ref={setBodyScrollNode}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          ...DOCK_SCROLLBAR_SX,
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        {!isEditing && !readOnly && userLotData?.id ? recordLeads : null}
        {!isEditing && !readOnly && userLotData?.id ? recordCampaigns : null}

        {officialUnit && !readOnly && !isEditing && !showListingBootstrap && (
          <OfficialUnitCadastralBlock
            officialUnit={officialUnit}
            compact={false}
            province={lotContext?.province || lotContext?.province_name}
          />
        )}


        {isEditing && !readOnly && !coreEdit && ufWizardStep === UF_WIZARD_STEP.BUILDING_TYPE && (
          <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2 }}>
            <BlockHeader
              title="Tipo de inmueble"
            />
            <Box sx={{ mt: 1.75 }}>
              <UfBuildingTypeStep
                buildingTypes={ufBuildingTypes}
                value={resolveSelectedPropertyTypeId({
                  propertyType: userLotForm.property_type,
                  buildingType: normalizeBuildingType(userLotForm.building_type),
                }, ufBuildingTypes)}
                disabled={!isEditing || wizardBusy}
                onChange={onBuildingTypeChange}
              />
            </Box>
          </Box>
        )}

        {showListingBootstrap && <UfListingStepSkeleton />}

        {readOnly && !coreEdit && !showListingBootstrap && (
          <BrandedUnitDetailSection
            unit={{ ...userLotForm, images: userLotData?.images, videos: userLotData?.videos, attachments: userLotData?.attachments }}
            lotContext={lotContext}
            accentColor={accentColor}
          />
        )}

        {((!readOnly && (ufWizardStep === UF_WIZARD_STEP.LISTING || !isEditing))
          || coreEdit)
          && !showListingBootstrap && (
          <>
        {!coreEdit ? (
        <Box sx={{ ...PRIVATE_BLOCK_SX, p: 2 }}>
            <BlockHeader
              icon={<LockOutlinedIcon sx={{ fontSize: 18, color: '#ABABAB', mt: 0.1 }} />}
              title={privateBlockTitle}
              subtitle="Campos privados que no se comparten con visitantes."
              action={(
                <BlockCollapseToggle
                  expanded={privateExpanded}
                  onToggle={() => setPrivateExpanded((v) => !v)}
                  labelExpanded="Contraer sección privada"
                  labelCollapsed="Expandir sección privada"
                />
              )}
            />
            <Collapse in={privateExpanded}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mt: 1.5 }}>
                <Box>
                  <TextField
                    label="Etiqueta interna"
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={isEditing ? 'Ej: Depto 2B, Casa del fondo, Local 3…' : ''}
                    value={userLotForm.unit_label || ''}
                    disabled={!isEditing}
                    inputProps={{ maxLength: 80 }}
                    onChange={(e) => setUserLotForm((p) => ({ ...p, unit_label: e.target.value }))}
                    sx={ufFieldSx}
                  />
                  {isEditing && (
                    <Typography component="span" sx={FIELD_HINT_SX}>
                      Para distinguir unidades en este lote (ej. &quot;Depto 1A&quot;).
                    </Typography>
                  )}
                </Box>

                {/* Invitado (F1): ve las notas del owner en solo lectura. */}
                {(canEdit || spaceGuest) && (
                  <Box>
                    <TextField
                      label="Notas privadas"
                      fullWidth
                      size="small"
                      variant="outlined"
                      multiline
                      rows={2}
                      value={userLotForm.observations}
                      disabled={!isEditing}
                      inputProps={{ maxLength: MAX_OBS }}
                      onChange={(e) => setUserLotForm((p) => ({ ...p, observations: e.target.value }))}
                      sx={ufFieldSx}
                    />
                    {isEditing && (
                      <Typography variant="caption" sx={{
                        color: (userLotForm.observations || '').length > MAX_OBS * 0.9 ? '#f87171' : '#ABABAB',
                        fontSize: '12px',
                        float: 'right',
                        mt: 0.5,
                        fontFamily: typography.fontFamily.primary,
                      }}>
                        {(userLotForm.observations || '').length}/{MAX_OBS}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        ) : null}

        {/* Bloque 2 — Publicación */}
        <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <BlockHeader
            icon={<PublicOutlinedIcon sx={{ fontSize: 18, color: '#ABABAB', mt: 0.1 }} />}
            title="Publicación"
            subtitle="Contenido descriptivo de la unidad."
            action={!isEditing ? <StockStatusBadge status={userLotForm.stock_status} /> : null}
          />

          <Box>
            <TextField
              label={<>Título {isEditing && <Typography component="span" sx={{ color: '#f87171', ml: 0.3, fontSize: '12px' }}>*</Typography>}</>}
              fullWidth
              size="small"
              variant="outlined"
              value={userLotForm.title}
              disabled={!isEditing}
              inputProps={{ maxLength: MAX_TITLE }}
              onChange={(e) => setUserLotForm((p) => ({ ...p, title: e.target.value }))}
              sx={ufFieldSx}
            />
            {isEditing && (
              <Typography variant="caption" sx={{
                color: (userLotForm.title || '').length > MAX_TITLE * 0.9 ? '#f87171' : '#ABABAB',
                fontSize: '12px',
                float: 'right',
                mt: 0.5,
                fontFamily: typography.fontFamily.primary,
              }}>
                {(userLotForm.title || '').length}/{MAX_TITLE}
              </Typography>
            )}
          </Box>

          <Box>
            <TextField
              label="Descripción"
              fullWidth
              size="small"
              variant="outlined"
              multiline
              rows={2}
              value={userLotForm.description}
              disabled={!isEditing}
              inputProps={{ maxLength: MAX_DESC }}
              onChange={(e) => setUserLotForm((p) => ({ ...p, description: e.target.value }))}
              sx={ufFieldSx}
            />
            {isEditing && (
              <Typography variant="caption" sx={{
                color: (userLotForm.description || '').length > MAX_DESC * 0.9 ? '#f87171' : '#ABABAB',
                fontSize: '12px',
                float: 'right',
                mt: 0.5,
                fontFamily: typography.fontFamily.primary,
              }}>
                {(userLotForm.description || '').length}/{MAX_DESC}
              </Typography>
            )}
          </Box>

          <Box sx={{ pt: 0.25 }}>
            <Typography
              sx={{
                color: colors.textPanelTitle,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                fontFamily: typography.fontFamily.primary,
                display: 'block',
                mb: 1.25,
                lineHeight: '16px',
              }}
            >
              Precio
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <TextField
                label="Monto"
                size="small"
                variant="outlined"
                type="text"
                inputMode="numeric"
                value={formatPriceInputDisplay(userLotForm.price)}
                disabled={!isEditing}
                onChange={(e) => setUserLotForm((p) => ({
                  ...p,
                  price: parsePriceInputValue(e.target.value),
                }))}
                InputProps={{ startAdornment: <Typography variant="caption" sx={secondaryTextSx}>$</Typography> }}
                sx={{ ...ufFieldSx, flex: '1.5 1 140px', minWidth: 0 }}
              />
              <FormControl size="small" sx={{ flex: '1 1 100px', minWidth: 0 }} disabled={!isEditing}>
                <InputLabel sx={inputLabelSx} shrink>
                  Moneda
                </InputLabel>
                <Select
                  value={userLotForm.currency || 'USD'}
                  label="Moneda"
                  variant="outlined"
                  notched
                  onChange={(e) => setUserLotForm((p) => ({ ...p, currency: e.target.value }))}
                  sx={selectSx}
                  MenuProps={listSelectMenuProps}
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <MenuItem key={c.value} value={c.value} sx={menuItemSx}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1.2 1 140px', minWidth: 0 }} disabled={!isEditing}>
                <InputLabel sx={inputLabelSx} shrink>
                  Operación
                </InputLabel>
                <Select
                  value={userLotForm.operation || ''}
                  label="Operación"
                  variant="outlined"
                  notched
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography
                          component="span"
                          sx={{ color: 'rgba(255,255,255,0.40)', fontSize: '14px' }}
                        >
                          Seleccionar
                        </Typography>
                      );
                    }
                    return OPERATION_LABEL[selected] || selected;
                  }}
                  onChange={(e) => setUserLotForm((p) => ({ ...p, operation: e.target.value }))}
                  sx={selectSx}
                  MenuProps={listSelectMenuProps}
                >
                  <MenuItem value="" sx={menuItemSx}>
                    <em>Sin operación</em>
                  </MenuItem>
                  {OPERATIONS.map((t) => (
                    <MenuItem key={t.value} value={t.value} sx={menuItemSx}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* En lectura el estado se ve como badge en el header de Publicación. */}
              {isEditing && (
              <FormControl size="small" sx={{ flex: '1.2 1 140px', minWidth: 0 }}>
                <InputLabel sx={inputLabelSx} shrink>
                  Estado
                </InputLabel>
                <Select
                  value={userLotForm.stock_status || 'available'}
                  label="Estado"
                  variant="outlined"
                  notched
                  onChange={(e) => setUserLotForm((p) => ({ ...p, stock_status: e.target.value }))}
                  sx={selectSx}
                  MenuProps={listSelectMenuProps}
                >
                  {STOCK_STATUSES.map((status) => (
                    <MenuItem key={status} value={status} sx={menuItemSx}>
                      {STOCK_STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              )}
            </Box>
          </Box>

          {/* «Precio vs. mercado» debajo del precio (solo lectura de la ficha). */}
          {!isEditing && !readOnly && userLotData?.id && renderMarketComparison
            ? renderMarketComparison({ onEdit: marketEditHandler })
            : null}

          {(!coreEdit || spaceGuest) ? (
          <>
          <Box sx={{ borderTop: '1px solid #383838', pt: 1.5 }}>
            <Typography
              sx={{
                color: colors.textPanelTitle,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                fontFamily: typography.fontFamily.primary,
                display: 'block',
                mb: 1.25,
                lineHeight: '16px',
              }}
            >
              Fotos
            </Typography>
            {userLotData?.id ? (
              <UserLotImages
                userLotId={userLotData.id}
                canEdit={!readOnly && (canEdit || coreEdit) && isEditing}
                initialImages={userLotData.images}
                ownerUserId={spaceOwnerUserId}
                additiveOnly={spaceGuestMediaAdditiveOnly}
              />
            ) : (
              <Typography variant="caption" sx={{
                color: 'rgba(255,255,255,0.25)', fontSize: '11px',
                fontStyle: 'italic', display: 'block', textAlign: 'center', py: 1,
              }}>
                Elegí el tipo y continuá para poder agregar fotos.
              </Typography>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid #383838', pt: 1.5 }}>
            <Typography
              sx={{
                color: colors.textPanelTitle,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                fontFamily: typography.fontFamily.primary,
                display: 'block',
                mb: 1.25,
                lineHeight: '16px',
              }}
            >
              Videos
            </Typography>
            {userLotData?.id ? (
              <UserLotVideos
                userLotId={userLotData.id}
                canEdit={!readOnly && (canEdit || coreEdit) && isEditing}
                initialVideos={userLotData.videos}
                ownerUserId={spaceOwnerUserId}
                additiveOnly={spaceGuestMediaAdditiveOnly}
              />
            ) : (
              <Typography variant="caption" sx={{
                color: 'rgba(255,255,255,0.25)', fontSize: '11px',
                fontStyle: 'italic', display: 'block', textAlign: 'center', py: 1,
              }}>
                Elegí el tipo y continuá para poder agregar videos.
              </Typography>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid #383838', pt: 1.5 }}>
            <Typography
              sx={{
                color: colors.textPanelTitle,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                fontFamily: typography.fontFamily.primary,
                display: 'block',
                mb: 1.25,
                lineHeight: '16px',
              }}
            >
              Adjuntos
            </Typography>
            {userLotData?.id ? (
              <UserLotAttachments
                userLotId={userLotData.id}
                canEdit={!readOnly && (canEdit || coreEdit) && isEditing}
                initialAttachments={userLotData.attachments}
                ownerUserId={spaceOwnerUserId}
                additiveOnly={spaceGuestMediaAdditiveOnly}
              />
            ) : (
              <Typography variant="caption" sx={{
                color: 'rgba(255,255,255,0.25)', fontSize: '11px',
                fontStyle: 'italic', display: 'block', textAlign: 'center', py: 1,
              }}>
                Elegí el tipo y continuá para poder agregar adjuntos.
              </Typography>
            )}
          </Box>
          </>
          ) : null}
        </Box>

        {/* Bloque 3 — Listas */}
        {!coreEdit ? (
        <Box sx={{ ...ORGANIZE_BLOCK_SX, p: 2 }}>
            <BlockHeader
              icon={<FolderOutlinedIcon sx={{ fontSize: 18, color: '#ABABAB' }} />}
              title="Listas"
              action={(
                <BlockCollapseToggle
                  expanded={organizeExpanded}
                  onToggle={() => setOrganizeExpanded((v) => !v)}
                  labelExpanded="Contraer listas"
                  labelCollapsed="Expandir listas"
                />
              )}
            />
            <Collapse in={organizeExpanded}>
              <Box sx={{ mt: 1.5 }}>
                {canEdit && isEditing ? (
                  <UserLotListMultiSelect
                    id="uf-list-select"
                    userLotLists={userLotLists}
                    value={Array.isArray(userLotForm.list_ids) ? userLotForm.list_ids : []}
                    onChange={(next) => setUserLotForm((p) => ({ ...p, list_ids: next }))}
                    onListCreated={onListCreated}
                    ownerUserId={spaceOwnerUserId}
                  />
                ) : (
                  userLotData && (
                    (() => {
                      const listsFromData = Array.isArray(userLotData.lists) ? userLotData.lists : [];
                      const idsFromData = Array.isArray(userLotData.list_ids)
                        ? userLotData.list_ids
                        : (userLotData.list_id ? [userLotData.list_id] : []);

                      const resolved = listsFromData.length
                        ? listsFromData
                        : idsFromData
                            .map((id) => userLotLists.find((r) => r.id === id))
                            .filter(Boolean);

                      const legacyName = userLotData.list_name;
                      const hasLegacyOnly = !resolved.length && !!legacyName;

                      const listChipSx = {
                        height: 26,
                        fontSize: '12px',
                        bgcolor: 'rgba(255,255,255,0.06)',
                        color: 'rgba(229,231,235,0.95)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        '& .MuiChip-label': { px: 1 },
                      };

                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, py: 0.5 }}>
                          {resolved.map((li) => (
                            <Chip key={li.id} size="small" label={li.name} sx={listChipSx} />
                          ))}
                          {hasLegacyOnly && (
                            <Chip size="small" label={legacyName} sx={listChipSx} />
                          )}
                          {!resolved.length && !hasLegacyOnly && (
                            <Typography variant="body2" sx={{ color: 'rgba(156,163,175,0.9)', fontSize: '13px' }}>
                              No hay listas asignadas
                            </Typography>
                          )}
                        </Box>
                      );
                    })()
                  )
                )}
              </Box>
            </Collapse>
          </Box>
        ) : null}
          </>
        )}

        {(!isEditing || ufWizardStep === UF_WIZARD_STEP.ATTRIBUTES)
          && !coreEdit
          && normalizeBuildingType(userLotForm.building_type)
          && (!readOnly || ufSchemaLoading || hasUfAttributeDisplayData(userLotForm)) && (
          <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2 }}>
            <BlockHeader
              title="Características"
              subtitle={isEditing
                ? 'Completá los datos del inmueble según su tipo.'
                : (readOnly
                  ? 'Atributos de la unidad compartida.'
                  : 'Detalle interno de la unidad.')}
            />
            <Box sx={{ mt: 1.75 }}>
              <UfAttributesForm
                schema={ufAttributeSchema}
                form={userLotForm}
                setForm={setUserLotForm}
                fieldSx={ufFieldSx}
                inputLabelSx={inputLabelSx}
                selectSx={selectSx}
                menuItemSx={menuItemSx}
                listSelectMenuProps={listSelectMenuProps}
                disabled={!isEditing || readOnly}
                lockedFieldIds={
                  parseOfficialBuiltSurfaceM2(officialUnit) != null
                    ? ['covered_surface']
                    : []
                }
                loading={ufSchemaLoading}
                hideEmpty={readOnly}
                accentColor={readOnly ? accentColor : null}
              />
            </Box>
          </Box>
        )}

        {isEditing && !readOnly && !coreEdit && ufWizardStep === UF_WIZARD_STEP.ATTRIBUTES
          && normalizeBuildingType(userLotForm.building_type) && (
          <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2 }}>
            <BlockHeader
              title="Información de la propiedad"
              subtitle="Datos comerciales, legales y de entrega. Opcional."
            />
            <Box sx={{ mt: 1.75 }}>
              <UfAttributesForm
                schema={propertyEditView.schema}
                form={propertyEditView.form}
                setForm={setPropertyEditForm}
                fieldSx={ufFieldSx}
                inputLabelSx={inputLabelSx}
                selectSx={selectSx}
                menuItemSx={menuItemSx}
                listSelectMenuProps={listSelectMenuProps}
                disabled={wizardBusy}
                loading={ufSchemaLoading}
                fieldErrors={userLotForm.property_errors}
              />
            </Box>
          </Box>
        )}

        {!isEditing && !coreEdit && propertyInfoView.hasData && (
          <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2 }}>
            <BlockHeader
              title="Información de la propiedad"
              subtitle={'Datos comerciales y legales de la unidad.'}
            />
            <Box sx={{ mt: 1.75 }}>
              <UfAttributesForm
                schema={propertyInfoView.schema}
                form={propertyInfoView.form}
                setForm={() => {}}
                fieldSx={ufFieldSx}
                inputLabelSx={inputLabelSx}
                selectSx={selectSx}
                menuItemSx={menuItemSx}
                listSelectMenuProps={listSelectMenuProps}
                disabled
                hideEmpty
                accentColor={readOnly ? accentColor : null}
              />
            </Box>
          </Box>
        )}

        {/* Guardar / Cancelar / Wizard */}
        {((canEdit && isEditing && !readOnly) || coreEdit) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              mt: 0.5,
              mx: -2,
              mb: -2,
              px: 2,
              py: 1.5,
              borderTop: '1px solid #383838',
              bgcolor: 'rgba(0,0,0,0.18)',
            }}
          >
            <Box>
              {!coreEdit && ufWizardStep > UF_WIZARD_STEP.BUILDING_TYPE && (
                <Button
                  onClick={onWizardBack}
                  disabled={wizardBusy}
                  sx={FORM_SECONDARY_BUTTON_SX}
                >
                  Volver
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onEditCancel}
                disabled={wizardBusy}
                sx={FORM_SECONDARY_BUTTON_SX}
              >
                Cancelar
              </Button>
              {!coreEdit && ufWizardStep < UF_WIZARD_STEP.ATTRIBUTES ? (
                <Button
                  onClick={onWizardContinue}
                  disabled={wizardBusy || (
                    (ufWizardStep === UF_WIZARD_STEP.BUILDING_TYPE
                      && !normalizeBuildingType(userLotForm.building_type))
                    || (ufWizardStep === UF_WIZARD_STEP.LISTING && !userLotForm.title?.trim())
                  )}
                  sx={FORM_PRIMARY_BUTTON_SX}
                >
                  Continuar
                </Button>
              ) : (
                <Button
                  startIcon={isSaving
                    ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
                    : <SaveIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={onSave}
                  disabled={wizardBusy || !userLotForm.title?.trim()}
                  sx={FORM_PRIMARY_BUTTON_SX}
                >
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </Button>
              )}
            </Box>
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default DetailsTab;
